import PackageIcon from "components/Icon/icons/package.svg";
import { fadeIn } from "react-animations";
import styled, { keyframes } from "styled-components";
import Icon from "../../../../components/Icon/Icon";

interface TilesGridProps {
  $columns: number;
}

export const InventoryTilesGrid = styled.div<TilesGridProps>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: 12px;
  padding: 12px 0 4px;

  ${({ theme, $columns }) => theme.md`
    grid-template-columns: repeat(${Math.min($columns, 4)}, minmax(0, 1fr));
  `}

  ${({ theme, $columns }) => theme.sm`
    grid-template-columns: repeat(${Math.min($columns, 3)}, minmax(0, 1fr));
  `}

  ${(props) => props.theme.xs`
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  `}
`;

interface TileContainerProps {
  $selected: boolean;
  $checked: boolean;
}

export const TileContainer = styled.div<TileContainerProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  border: 0.5px solid
    ${({ theme, $checked }) => ($checked ? theme.primary : theme.rowBorder)};
  border-radius: 7px;
  background-color: ${({ theme, $checked, $selected }) =>
    ($checked && theme.checkedRowBg) ||
    ($selected && theme.selectedRowBg) ||
    theme.rowBg};
  animation: 0.3s ${keyframes`${fadeIn}`};

  &:hover {
    background-color: ${({ theme, $checked }) =>
      $checked ? theme.checkedRowBg : theme.selectedRowBg};
  }
`;

export const TilePhotoArea = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background-color: ${({ theme }) => theme.skeletonBg};
`;

export const TilePhoto = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const TilePhotoPlaceholder = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  height: 32px;
  width: 32px;
  transform: translate(-50%, -50%);
  background-color: ${({ theme }) => theme.parcelIconColor};
  -webkit-mask: url("${PackageIcon}") center/cover no-repeat;
  mask: url("${PackageIcon}") center/cover no-repeat;
`;

export const TileCheckboxOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  padding: 8px 10px 10px 8px;
  cursor: pointer;
  border-bottom-right-radius: 7px;
  background-color: ${({ theme }) => theme.surfaceOverlay60};
`;

export const TilePhotoCountBadge = styled.button`
  position: absolute;
  right: 6px;
  bottom: 6px;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: none;
  border-radius: 7px;
  background-color: ${({ theme }) => theme.surfaceOverlay60};
  color: ${({ theme }) => theme.textSecondary};
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
`;

export const BadgePhotosIcon = styled(Icon)`
  width: 12px;
  height: 12px;
  background-color: ${({ theme }) => theme.textSecondary};
`;

export const TileBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px 10px;
  text-align: left;
`;

export const TileDescriptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;

  img {
    width: 14px;
    height: 14px;
    margin-top: 2px;
  }
`;

export const TileDescriptionText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  text-align: left;
  font-size: 13px;
  line-height: 17px;
  color: ${({ theme }) => theme.textMain};
`;

export const TileCommentIcon = styled(Icon)`
  width: 14px;
  height: 14px;
  margin-top: 2px;
  background-color: ${({ theme }) => theme.black50};
`;

export const TileQuantityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.black50};
`;

export const TileMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 6px;
  margin-top: auto;
  font-size: 12px;
  color: ${({ theme }) => theme.textMuted};
`;

export const TileWeight = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.textSecondary};
`;
